/**
 * LangSwitcher - segmented VI | EN button in header right-cluster.
 *
 * Writes user choice to `localStorage.asklaw_lang` via `useLang().setLang`,
 * which dispatches `asklaw:lang` same-tab event so all subscribers re-render
 * (footer disclaimer, /me page, auth disclaimer, nav labels...).
 *
 * Compact variant for mobile: same control, smaller padding.
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { useLang, type Lang } from '../use-lang';

type Props = {
  variant?: 'compact' | 'default';
};

export function LangSwitcher({ variant = 'default' }: Props) {
  const { lang, setLang } = useLang();
  const compact = variant === 'compact';
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onLanding = pathname === '/' || pathname === '/vi' || pathname === '/vi/';

  const choose = (opt: Lang) => {
    setLang(opt);
    if (onLanding) navigate(opt === 'vi' ? '/vi/' : '/');
  };

  return (
    <div
      role="radiogroup"
      aria-label={lang === 'vi' ? 'Ngôn ngữ' : 'Language'}
      style={{
        display: 'inline-flex',
        border: '1px solid var(--asklaw-border)',
        borderRadius: 999,
        background: 'transparent',
        padding: 2,
        fontFamily: 'var(--font-mono)',
      }}
    >
      {(['vi', 'en'] as Lang[]).map((opt) => {
        const active = lang === opt;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => choose(opt)}
            style={{
              padding: compact ? '4px 9px' : '5px 11px',
              border: 0,
              borderRadius: 999,
              background: active ? 'var(--asklaw-ink)' : 'transparent',
              color: active ? 'var(--asklaw-cream)' : 'var(--asklaw-muted)',
              fontWeight: 600,
              fontSize: compact ? 11 : 12,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'background 160ms ease, color 160ms ease',
            }}
          >
            {opt === 'vi' ? 'VI' : 'EN'}
          </button>
        );
      })}
    </div>
  );
}
